import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '@/features/auth/AuthContext';
import { PERMISSIONS } from '@/features/auth/permissions';
import { LoginPage } from '@/features/auth/LoginPage';
import { ViewAsBanner } from '@/features/viewAs/ViewAsBanner';
import { AppRoutes } from './AppRoutes';
import { readDetectionId } from '@/features/chat/detectionLink';
import { isSlackConnectPath } from '@/features/slackConnect/slackConnect';
import { SlackConnectPage } from '@/features/slackConnect/SlackConnectPage';

// The whole app (chat + result panel + echarts, ~700KB gzip) lives behind auth;
// lazy-load it so the login screen ships only the auth shell.
const HomePage = lazy(() => import('@/features/chat/components/HomePage').then((m) => ({ default: m.HomePage })));

function Centered() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

/**
 * Signing in lands on Overview — but ONLY from the bare root.
 *
 * A deep link has to keep working: `/chat/:id` is how a conversation gets shared,
 * and the login gate is deliberately not a route so that finishing sign-in returns
 * to the linked chat (see below). So the redirect is conditional on there being no
 * link to honour, and it fires once — `/` reached LATER is the empty new-chat
 * canvas and must stay reachable.
 *
 * It runs while `HomePage` is still lazy-loading behind the Suspense spinner, so
 * there is no flash of the empty canvas on the way through.
 *
 * Which of the two landings a tenant gets is the whole point of `insightsEnabled`:
 * on, and signing in opens the day's cards; off, and it opens the new-chat canvas
 * exactly as it always did.
 *
 * Exported for the lifecycle stories: a reload that interrupted a turn lands on
 * exactly the bare root this component redirects away from, so any test of that
 * reload without this in the tree is testing a different app.
 */
export function LandOnOverview({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  // The ROUTER's location, not window.location: identical under BrowserRouter
  // (the app), but it is the router being redirected — and it is what a memory
  // router in a test actually carries.
  const { pathname, search } = useLocation();
  const done = useRef(false);
  useEffect(() => {
    // Nothing to land on without the feature — `/` is then the whole app entry. NOT
    // latched in that case, deliberately: were the claim to arrive a render after the
    // session stops loading, latching here would burn the one shot and a tenant WITH
    // the feature would silently keep the chat canvas.
    if (done.current || !enabled) return;
    done.current = true; // StrictMode mounts effects twice
    // The search survives the redirect: after a mid-turn reload `?tab=` is the
    // only remaining record of which result window was open, and the resume
    // reads it from wherever it lands — including here.
    // A detection link names where it is going (`/?detection_id=`), and a link the
    // user followed outranks the default landing: redirected, it would open on the
    // Insights canvas and the detection's card would never draw.
    if (pathname === '/' && !readDetectionId(new URLSearchParams(search)))
      navigate({ pathname: '/overview', search }, { replace: true });
    // One shot from wherever the app came up — a LATER walk to "/" must not bounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, enabled]);
  return null;
}

export default function App() {
  const { user, loading, insightsEnabled, viewingAs, can } = useAuth();
  // The user's permission alone: no tenant is held back from a screen (product,
  // 2026-10-28 — the `impersonationEnabled` tenant flag was deleted). Computed
  // here because App is the one place already holding the session; AppRoutes
  // stays a pure route table.
  const impersonationAllowed = can(PERMISSIONS.brandImpersonation);

  if (loading) return <Centered />;
  // Before the login gate, for signed-in and signed-out visitors alike: the Slack
  // connect flow does its own sign-in, and a signed-in visitor must still reach it.
  if (isSlackConnectPath(window.location.pathname)) return <SlackConnectPage />;
  // The login gate is deliberately NOT a route: a signed-out deep link keeps its
  // URL, so finishing the sign-in lands on the chat that was linked (no /login
  // detour to come back from).
  if (!user) return <LoginPage />;
  return (
    <BrowserRouter>
      {/* Above the routes, not inside a screen: whichever screen is drawn — and
          whichever one is added later — must carry it. */}
      {viewingAs && <ViewAsBanner />}
      <LandOnOverview enabled={insightsEnabled} />
      <Suspense fallback={<Centered />}>
        <AppRoutes
          screen={<HomePage />}
          insights={insightsEnabled}
          impersonation={impersonationAllowed}
          attackSurface={can(PERMISSIONS.attackSurface)}
        />
      </Suspense>
    </BrowserRouter>
  );
}
